import type { LeadStatus, LeadTier, CompanySize, LeadSource, InteractionType, ScoreLabel, OrganizationType, OrganizationTier } from "./types";

// ─── Status ────────────────────────────────────────────────────────────────

export const LEAD_STATUSES: LeadStatus[] = [
  "NEW", "CONTACTED", "QUALIFIED", "DEMO_SCHEDULED",
  "DEMO_COMPLETED", "NEGOTIATION", "WON", "LOST",
];

export const STATUS_COLOR: Record<LeadStatus, string> = {
  NEW: "bg-blue-100 text-blue-800",
  CONTACTED: "bg-yellow-100 text-yellow-800",
  QUALIFIED: "bg-purple-100 text-purple-800",
  DEMO_SCHEDULED: "bg-indigo-100 text-indigo-800",
  DEMO_COMPLETED: "bg-cyan-100 text-cyan-800",
  NEGOTIATION: "bg-orange-100 text-orange-800",
  WON: "bg-green-100 text-green-800",
  LOST: "bg-red-100 text-red-800",
};

export const VALID_TRANSITIONS: Record<LeadStatus, LeadStatus[]> = {
  NEW: ["CONTACTED", "LOST"],
  CONTACTED: ["QUALIFIED", "LOST"],
  QUALIFIED: ["DEMO_SCHEDULED", "LOST"],
  DEMO_SCHEDULED: ["DEMO_COMPLETED", "LOST"],
  DEMO_COMPLETED: ["NEGOTIATION", "WON", "LOST"],
  NEGOTIATION: ["WON", "LOST"],
  WON: [],
  LOST: ["NEW"],
};

// ─── Tiers ─────────────────────────────────────────────────────────────────

export const LEAD_TIERS: LeadTier[] = ["SEED", "SCALE", "ENTERPRISE"];

export const TIER_EMOJI: Record<LeadTier, string> = {
  SEED: "🌱",
  SCALE: "🚀",
  ENTERPRISE: "🏢",
};

// ─── Company Sizes ─────────────────────────────────────────────────────────

export const COMPANY_SIZES: CompanySize[] = [
  "XS", "SM", "MD", "LG", "XL",
];

/** Map GraphQL enum key → human-readable range */
export const SIZE_LABELS: Record<CompanySize, string> = {
  XS: "1–25",
  SM: "26–100",
  MD: "101–250",
  LG: "251–499",
  XL: "500+",
};

// ─── Sources ───────────────────────────────────────────────────────────────

export const LEAD_SOURCES: LeadSource[] = ["WEBSITE", "MANUAL", "REFERRAL"];

export const SOURCE_EMOJI: Record<LeadSource, string> = {
  WEBSITE: "🌐",
  MANUAL: "✏️",
  REFERRAL: "🤝",
};

// ─── Interaction Types ─────────────────────────────────────────────────────

export const INTERACTION_TYPES: InteractionType[] = [
  "NOTE", "EMAIL", "CALL", "MEETING", "STATUS_CHANGE",
];

export const INTERACTION_EMOJI: Record<InteractionType, string> = {
  NOTE: "📝",
  EMAIL: "📧",
  CALL: "📞",
  MEETING: "🤝",
  STATUS_CHANGE: "🔄",
};

// ─── Score Thresholds ──────────────────────────────────────────────────────

export const SCORE_HOT_THRESHOLD = 70;
export const SCORE_WARM_THRESHOLD = 30;

export function getScoreLabel(score: number): ScoreLabel {
  if (score >= SCORE_HOT_THRESHOLD) return "hot";
  if (score >= SCORE_WARM_THRESHOLD) return "warm";
  return "cold";
}

export const SCORE_COLORS: Record<ScoreLabel, string> = {
  hot: "bg-red-100 text-red-800",
  warm: "bg-amber-100 text-amber-800",
  cold: "bg-slate-100 text-slate-600",
};

// ─── Organization (for lead conversion) ────────────────────────────────────

export const ORGANIZATION_TYPES: OrganizationType[] = ["NGO", "NPO", "COMMERCIAL"];

export const ORGANIZATION_TIERS: OrganizationTier[] = ["SEED", "SCALE", "ENTERPRISE"];

/** Map lead tier → default org tier */
export const LEAD_TIER_TO_ORG_TIER: Record<LeadTier, OrganizationTier> = {
  SEED: "SEED",
  SCALE: "SCALE",
  ENTERPRISE: "ENTERPRISE",
};

// ─── Validation helpers ────────────────────────────────────────────────────

/** Recommended company sizes per tier — SEED=1-25, SCALE=26-250, ENTERPRISE=251+ */
export const TIER_RECOMMENDED_SIZES: Record<LeadTier, CompanySize[]> = {
  SEED: ["XS"],
  SCALE: ["SM", "MD"],
  ENTERPRISE: ["LG", "XL"],
};

/** Check if tier/size pairing is a mismatch (returns true if warning needed) */
export function isTierSizeMismatch(tier: LeadTier, size: CompanySize): boolean {
  return !TIER_RECOMMENDED_SIZES[tier].includes(size);
}

/** Simple email format validation (RFC-lite) */
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/** Extract first usable error message from an Apollo/GraphQL error */
export function extractErrorMessage(err: unknown): string | null {
  if (!err || typeof err !== "object") return null;
  const error = err as { graphQLErrors?: Array<{ message?: string }>; message?: string };
  if (error.graphQLErrors?.length) {
    return error.graphQLErrors[0].message ?? null;
  }
  if (error.message) {
    // Strip "ApolloError: " prefix if present
    return error.message.replace(/^ApolloError:\s*/i, "");
  }
  return null;
}
